import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ArrowsLeftRightIcon, ArrowUDownLeftIcon } from '@phosphor-icons/react'

import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import { transactionTypes } from '@/returns/constants'

import ReasonsCard from '../ReasonsCard/ReasonsCard'
import ReturnSalesRatioCard from '../ReturnSalesRatioCard/ReturnSalesRatioCard'
import ReturnsAndExchangesRatioCard from '../ReturnsAndExchangesRatioCard/ReturnsAndExchangesRatioCard'
import SizingIssuesCard from '../SizingIssuesCard/SizingIssuesCard'
import VariantsCard from '../VariantsCard/VariantsCard'
import ProductMetricsSkeleton from './ProductMetricsSkeleton/ProductMetricsSkeleton'
import { getVariantBasePath } from './helpers'
import * as Styles from './styles'

const ProductMetrics = ({
  loading,
  product,
  returnPortalId,
  commonFilters,
  returnSalesRatioFilters,
  setReturnSalesRatioFilters,
  returnsAndExchangesRatioFilters,
  setReturnsAndExchangesRatioFilters,
  productPosition,
  salesOrdersDataActivated,
  categoryId,
  categoryPosition,
  typeId,
  typePosition,
  collectionId,
  collectionPosition,
  variantsOrdering,
  isUnifiedDashboard,
}) => {
  const { t } = useTranslation()
  const navigation = useNavigate()
  const productId = product.id

  const getAdditionalFilters = (additionalFilters = {}) => ({
    ...additionalFilters,
    ...(collectionId && { collectionId }),
    ...(typeId && { typeId }),
    ...(categoryId && { categoryId }),
  })

  const { data: returnsAndExchangesRatioData, loading: loadingReturnsAndExchangesRatio } = useMetrics({
    returnPortalId,
    service: metricsServices.getProductReturnsAndExchangesRatio,
    entityId: productId,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: getAdditionalFilters(returnsAndExchangesRatioFilters),
  })

  const { data: returnSalesRatioData, loading: loadingReturnSalesRatio } = useMetrics({
    returnPortalId,
    service: metricsServices.getProductReturnsSalesRatio,
    entityId: productId,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: getAdditionalFilters(returnSalesRatioFilters),
    doRequest: !!salesOrdersDataActivated,
  })

  const { data: topVariantsData, loading: loadingTopVariants } = useMetrics({
    returnPortalId,
    service: metricsServices.getProductTopVariants,
    entityId: productId,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: getAdditionalFilters({ ordering: variantsOrdering }),
  })

  const { data: topReturnReasonsData, loading: loadingTopReturnReasons } = useMetrics({
    returnPortalId,
    service: metricsServices.getProductTopReasons,
    entityId: productId,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: getAdditionalFilters({ transactionType: transactionTypes.refund }),
  })

  const { data: topExchangeReasonsData, loading: loadingTopExchangeReasons } = useMetrics({
    returnPortalId,
    service: metricsServices.getProductTopReasons,
    entityId: productId,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: getAdditionalFilters({ transactionType: transactionTypes.exchange }),
  })

  const handleOnClickVariant = variantPosition => {
    const pathname = getVariantBasePath({
      categoryId,
      categoryPosition,
      collectionId,
      collectionPosition,
      typeId,
      typePosition,
      isUnifiedDashboard,
    })
      .replace(':productPosition', productPosition)
      .replace(':variantPosition', variantPosition)

    navigation({
      pathname,
      search: new URLSearchParams(window.location.search).toString(),
    })
  }

  if (loading) return <ProductMetricsSkeleton />

  return (
    <>
      <SizingIssuesCard product={product} />
      <ReturnSalesRatioCard
        filters={{ ...commonFilters, ...returnSalesRatioFilters }}
        data={returnSalesRatioData}
        loading={loadingReturnSalesRatio}
        salesOrdersDataActivated={salesOrdersDataActivated}
        setChartFilters={setReturnSalesRatioFilters}
      />
      <ReturnsAndExchangesRatioCard
        data={returnsAndExchangesRatioData}
        loading={loadingReturnsAndExchangesRatio}
        filters={{ ...commonFilters, ...returnsAndExchangesRatioFilters }}
        setChartFilters={setReturnsAndExchangesRatioFilters}
      />
      <VariantsCard
        data={topVariantsData?.results}
        loading={loadingTopVariants}
        onClickVariant={handleOnClickVariant}
        salesOrdersDataActivated={salesOrdersDataActivated}
        ordering={variantsOrdering}
      />
      <Styles.ColumnsContainer templateColumns={'1fr 1fr'}>
        <ReasonsCard
          title={t('Motivos de devolución')}
          icon={<ArrowUDownLeftIcon size={24} />}
          reasons={topReturnReasonsData?.results}
          loading={loadingTopReturnReasons}
          reasonType={transactionTypes.refund}
          shouldShowExport={false}
        />
        <ReasonsCard
          title={t('Motivos de cambio')}
          icon={<ArrowsLeftRightIcon size={24} />}
          reasons={topExchangeReasonsData?.results}
          loading={loadingTopExchangeReasons}
          reasonType={transactionTypes.exchange}
          shouldShowExport={false}
        />
      </Styles.ColumnsContainer>
    </>
  )
}

export default ProductMetrics
